/**
 * Scores every (Feature, FlagCatalogRow) pair on IDF-weighted token overlap
 * and returns ranked candidates for auto-linking (Task 4) and human/AI
 * review (Tasks 9-10).
 *
 * @see docs/superpowers/specs/2026-08-09-atlas-flag-cluster-linking-design.md §1
 */
import { tokenizeIdentifier, tokenizeProse } from './tokenize.js';
import { computeIdf, idfWeightedOverlap } from './idf.js';
import type { Domain, Feature, FlagCatalogRow } from '../types.js';

export interface MatchCandidate {
  featureId: string;
  flagId: string;
  score: number; // 0-1
  matchedOn: Array<'identifier' | 'description' | 'domain' | 'killswitchRef'>;
}

/** Score at/above this auto-links (`Feature.gatingFlagIds`) at build time — bypasses human review. */
export const AUTO_LINK_THRESHOLD = 0.72;
/** Score at/above this (but below AUTO_LINK_THRESHOLD) surfaces as a review-queue candidate. */
export const CANDIDATE_THRESHOLD = 0.4;
/** Maximum combined contribution from corroborating signals (domain proximity, killswitchRef propagation) — never sufficient alone to cross a threshold from zero. */
export const CORROBORATING_BOOST_CAP = 0.2;

/** Build the IDF corpus from every flagId/description/label/entityIds token set in the manifest, one "document" per field per record. */
function buildCorpusIdf(features: readonly Feature[], flagRows: readonly FlagCatalogRow[]): Map<string, number> {
  const docs: string[][] = [];
  for (const f of flagRows) {
    docs.push(tokenizeIdentifier(f.flagId));
    if (f.description) docs.push(tokenizeProse(f.description));
  }
  for (const feature of features) {
    docs.push(tokenizeProse(feature.label));
    if (feature.entityIds.length > 0) docs.push(feature.entityIds.flatMap((id) => tokenizeProse(id)));
  }
  return computeIdf(docs);
}

/** S1 (identifier↔label) and S2 (description↔label+entityIds), combined via max — see spec §1. */
function scorePair(
  feature: Feature,
  flagRow: FlagCatalogRow,
  idf: Map<string, number>,
): { score: number; matchedOn: MatchCandidate['matchedOn'] } {
  const flagIdTokens = tokenizeIdentifier(flagRow.flagId);
  const labelTokens = tokenizeProse(feature.label);
  const s1 = idfWeightedOverlap(flagIdTokens, labelTokens, idf);

  const descTokens = flagRow.description ? tokenizeProse(flagRow.description) : [];
  const labelAndEntityTokens = [...labelTokens, ...feature.entityIds.flatMap((id) => tokenizeProse(id))];
  const s2 = descTokens.length > 0 ? idfWeightedOverlap(descTokens, labelAndEntityTokens, idf) : 0;

  const matchedOn: MatchCandidate['matchedOn'] = [];
  if (s1 >= s2 && s1 > 0) matchedOn.push('identifier');
  if (s2 >= s1 && s2 > 0) matchedOn.push('description');

  return { score: Math.max(s1, s2), matchedOn };
}

/** Score a single feature/flag pair on demand — same math `matchFeatureFlags` uses internally, exposed
 *  for the frontend's "Ask Atlas to link this" flow (Task 9), which needs to score one fixed record
 *  against every counterpart without waiting for a full manifest rebuild. */
export function scoreFeatureFlagPair(
  feature: Feature,
  flagRow: FlagCatalogRow,
  features: readonly Feature[],
  flagRows: readonly FlagCatalogRow[],
): { score: number; matchedOn: MatchCandidate['matchedOn'] } {
  const idf = buildCorpusIdf(features, flagRows);
  return scorePair(feature, flagRow, idf);
}

export function matchFeatureFlags(
  features: readonly Feature[],
  flagRows: readonly FlagCatalogRow[],
  domains: readonly Domain[],
): MatchCandidate[] {
  const idf = buildCorpusIdf(features, flagRows);
  const base: MatchCandidate[] = [];

  for (const feature of features) {
    for (const flagRow of flagRows) {
      const { score, matchedOn } = scorePair(feature, flagRow, idf);
      if (score > 0) base.push({ featureId: feature.id, flagId: flagRow.flagId, score, matchedOn });
    }
  }

  const boosted = applyCorroboratingBoosts(base, features, flagRows, domains);
  return boosted
    .filter((c) => c.score >= CANDIDATE_THRESHOLD)
    .sort((a, b) => b.score - a.score);
}

function applyCorroboratingBoosts(
  candidates: readonly MatchCandidate[],
  features: readonly Feature[],
  flagRows: readonly FlagCatalogRow[],
  domains: readonly Domain[],
): MatchCandidate[] {
  const featureById = new Map(features.map((f) => [f.id, f]));
  const flagRowById = new Map(flagRows.map((r) => [r.flagId, r]));
  const domainDocRefById = new Map(domains.map((d) => [d.id, d.docRef]));

  // Parents confidently linked (>= AUTO_LINK_THRESHOLD, pre-boost) to a single Feature, so their
  // children can inherit that Feature as a corroborating signal (spec §1, killswitchRef propagation).
  const confidentParentFeatureId = new Map<string, string>();
  for (const c of candidates) {
    if (c.score >= AUTO_LINK_THRESHOLD) confidentParentFeatureId.set(c.flagId, c.featureId);
  }

  return candidates.map((c) => {
    const feature = featureById.get(c.featureId);
    const flagRow = flagRowById.get(c.flagId);
    if (!feature || !flagRow) return c;

    let boost = 0;
    const matchedOn = [...c.matchedOn];

    // Domain proximity: flag's source doc matches the Feature's domain doc.
    const featureDocRef = domainDocRefById.get(feature.domainId);
    if (featureDocRef && featureDocRef === flagRow.docRef) {
      boost += 0.1;
      matchedOn.push('domain');
    }

    // killswitchRef propagation: a parent killswitch already confidently linked to this same Feature.
    const hasCorroboratingParent = (flagRow.killswitchRef ?? []).some(
      (parentId) => confidentParentFeatureId.get(parentId) === c.featureId,
    );
    if (hasCorroboratingParent) {
      boost += 0.1;
      matchedOn.push('killswitchRef');
    }

    boost = Math.min(boost, CORROBORATING_BOOST_CAP);
    return boost > 0 ? { ...c, score: Math.min(1, c.score + boost), matchedOn } : c;
  });
}
